import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { TABS, INITIAL_FORM_STATE } from "./provider-create/types";
import type { Tab, ProviderFormState } from "./provider-create/types";
import TabStammdaten from "./provider-create/TabStammdaten";
import TabKontakte from "./provider-create/TabKontakte";
import TabService from "./provider-create/TabService";
import TabZugangsdaten from "./provider-create/TabZugangsdaten";

export default function OperatorProviderCreate() {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { operatorFetch } = useOperatorApi();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("stammdaten");
  const [form, setForm] = useState<ProviderFormState>(INITIAL_FORM_STATE);

  const update = (field: string, value: any) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleArrayItem = (field: string, item: string) => {
    setForm((f) => {
      const arr = (f as any)[field] as string[];
      return {
        ...f,
        [field]: arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item],
      };
    });
  };

  async function handleSubmit() {
    if (!form.company_name || !form.contact_name || !form.contact_email) {
      toast.error(t("op.createRequiredFields"));
      setActiveTab("stammdaten");
      return;
    }
    if (form.create_account && !form.login_email) {
      toast.error(t("op.createLoginEmailRequired"));
      setActiveTab("zugangsdaten");
      return;
    }
    setSaving(true);
    try {
      await operatorFetch("providers", {
        method: "POST",
        body: {
          company_name: form.company_name,
          legal_name: form.legal_name || undefined,
          contact_name: form.contact_name,
          contact_role: form.contact_role || undefined,
          contact_email: form.contact_email,
          phone: form.phone || undefined,
          website: form.website || undefined,
          invoice_email: form.invoice_email || undefined,
          address_line1: form.address_line1 || undefined,
          postal_code: form.postal_code || undefined,
          city: form.city || undefined,
          country: form.country,
          vat_id: form.vat_id || undefined,
          geschaeftsfuehrer: form.geschaeftsfuehrer || undefined,
          gruendungsjahr: form.gruendungsjahr ? Number(form.gruendungsjahr) : undefined,
          bonitaet_rating: form.bonitaet_rating || undefined,
          region: form.region || undefined,
          service_regions: form.service_regions,
          service_locations: form.service_locations.filter((l) => l.city),
          remote_supported: form.remote_supported,
          sla_tier: form.sla_tier,
          sla_response_hours: form.sla_response_hours,
          weekly_lead_limit: form.weekly_lead_limit,
          monthly_opportunity_limit: form.monthly_opportunity_limit,
          categories: form.categories,
          stack_tags: form.selectedStackTags,
          quality_score: form.quality_score,
          acceptance_rate: form.acceptance_rate,
          avg_response_time_hours: form.avg_response_time_hours,
          is_approved: form.is_approved,
          billing_status: form.billing_status,
          create_account: form.create_account,
          login_email: form.login_email || undefined,
          initial_password: form.initial_password || undefined,
          portal_access_enabled: form.portal_access_enabled,
          contacts: form.contacts.filter((c) => c.name && c.email),
        },
      });
      toast.success(t("op.createSuccess"));
      navigate("/operator/providers");
    } catch (err: any) {
      toast.error(err.message || t("op.createError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <OperatorLayout title={t("op.createTitle")} subtitle={t("op.createSubtitle")}>
      <div className="max-w-3xl space-y-6">
        {/* Tab Navigation */}
        <div className="flex border-b border-border">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-5 py-3 text-sm transition-colors border-b-2 -mb-px",
                activeTab === tab.id
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "stammdaten" && <TabStammdaten form={form} update={update} />}
        {activeTab === "kontakte" && <TabKontakte form={form} update={update} />}
        {activeTab === "service" && <TabService form={form} update={update} toggleArrayItem={toggleArrayItem} />}
        {activeTab === "zugangsdaten" && <TabZugangsdaten form={form} update={update} />}

        {/* Actions */}
        <div className="flex gap-4 pb-8">
          <Button variant="commit" onClick={handleSubmit} disabled={saving}>
            {saving ? t("common.loading") : t("op.createSubmit")}
          </Button>
          <Button variant="quiet" onClick={() => navigate("/operator/providers")}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </OperatorLayout>
  );
}